/**
 * Пополнение справочника кредиторов из дел (решение заказчика 24.09.2026).
 *
 * Кредитор, набранный в деле вручную, попадает в общий справочник, чтобы
 * в следующем деле его можно было выбрать по первым буквам вместе с ИНН
 * и адресом. Здесь — только отбор и сравнение; запись делает сервер.
 *
 * ГРАЖДАНЕ В СПРАВОЧНИК НЕ ПОПАДАЮТ. Кредитором бывает и частное лицо
 * (заём у знакомого), и тогда в строке его ФИО и домашний адрес. Справочник
 * виден всем сотрудникам во всех делах — сохранить туда такую строку значит
 * разнести персональные данные третьего лица по чужим делам. Поэтому строка
 * с 12-значным ИНН или с наименованием, похожим на ФИО, пропускается.
 */

import type { Creditor } from '../types/creditor.js';
import { isValidInn } from '../validators/inn.js';

/** Признаки организации в наименовании: форма собственности или род деятельности. */
const ORGANIZATION_MARKERS =
  /(^|[\s«"(])(ооо|оао|зао|пао|нао|ао|мфк|мкк|мфо|кпк|пко|гуп|муп|фгуп|тсж|снт|нко)([\s»")]|$)|банк|общество|компани|кредит|финанс|фонд|инспекц|управлени|служб|организац|кооператив|учрежден|агентств|коллектор|[«"]/i;

/** «Иванов Иван Иванович», «Иванов И. И.», «Петрова-Водкина Анна». */
const PERSON_NAME = /^[А-ЯЁ][а-яё]+(-[А-ЯЁ][а-яё]+)?(\s+([А-ЯЁ][а-яё]+|[А-ЯЁ]\.)){1,2}$/;

/** Похоже ли наименование на ФИО гражданина. */
export function looksLikePersonName(name: string): boolean {
  const value = name.trim().replace(/\s+/g, ' ').replace(/\.\s*(?=[А-ЯЁ]\.)/g, '. ');
  if (ORGANIZATION_MARKERS.test(value)) return false;
  return PERSON_NAME.test(value);
}

/**
 * Ключ сравнения наименований: регистр, «ё», кавычки и лишние пробелы
 * не делают кредитора другим. «ПАО  "Сбербанк"» и «ПАО Сбербанк» — одно.
 */
export function creditorNameKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[«»"'„“”`]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Формы собственности и виды организаций — полностью и сокращённо. */
const LEGAL_FORMS = [
  'публичное акционерное общество',
  'непубличное акционерное общество',
  'открытое акционерное общество',
  'закрытое акционерное общество',
  'акционерное общество',
  'общество с ограниченной ответственностью',
  'микрофинансовая компания',
  'микрокредитная компания',
  'специализированное финансовое общество',
  'небанковская кредитная организация',
  'профессиональная коллекторская организация',
  'кредитный потребительский кооператив',
  'коммерческий банк',
];
const LEGAL_ABBREVIATIONS = /(^|\s)(ооо|оао|зао|пао|нао|ао|мфк|мкк|мфо|сфо|нко|кпк|пко|кб|банк)(?=\s|$)/g;

/**
 * Полные дубли в таблице кредиторов (просьба заказчика 25.10.2026): строки,
 * у которых совпадают ВСЕ графы — содержание, кредитор, ИНН, адрес,
 * основание и его реквизиты, суммы, документ, признаки. Так бывает, когда
 * один и тот же отчёт или постановление внесли дважды. Регистр и лишние
 * пробелы не различаются; пустые строки (без кредитора и суммы) не считаются.
 * Возвращает группы номеров строк (с нуля), по группе на каждый дубль.
 */
export function findDuplicateCreditors(
  creditors: Array<{
    obligationType?: string;
    name?: string;
    inn?: string;
    address?: string;
    basis?: string;
    basisDetails?: string;
    amountDebt?: number;
    amountOverdue?: number;
    confirmingDocument?: string;
    isBusiness?: boolean;
    overdueOverThreeMonths?: boolean;
  }>,
): number[][] {
  const text = (value: string | undefined) => (value ?? '').toLowerCase().replace(/ё/g, 'е').replace(/\s+/g, ' ').trim();
  const groups = new Map<string, number[]>();
  creditors.forEach((c, index) => {
    if (!text(c.name) && !c.amountDebt) return;
    const key = JSON.stringify([
      text(c.obligationType), text(c.name), text(c.inn), text(c.address), text(c.basis), text(c.basisDetails),
      c.amountDebt ?? 0, c.amountOverdue ?? null, text(c.confirmingDocument), Boolean(c.isBusiness), Boolean(c.overdueOverThreeMonths),
    ]);
    groups.set(key, [...(groups.get(key) ?? []), index]);
  });
  return [...groups.values()].filter((group) => group.length > 1);
}

/**
 * Ключ для сопоставления кредитора из отчёта со справочником: только
 * «собственное имя» организации. Бюро и справочник пишут одно и то же
 * по-разному — «МФК Быстроденьги (ООО)» и «ООО МФК «Быстроденьги»»,
 * «ВТБ (ПАО)» и «Банк ВТБ (ПАО)», — а ключ у них один: «быстроденьги», «втб».
 *
 * Прежние наименования в скобках «(ранее - …)» отбрасываются: иначе
 * переименованная организация не узнаётся по новому имени.
 */
export function creditorCoreKey(name: string): string {
  let value = name.toLowerCase().replace(/ё/g, 'е');

  // «(ранее - …)» со вложенными скобками — до парной закрывающей
  const earlier = value.search(/\(\s*ранее/);
  if (earlier >= 0) {
    let depth = 0;
    let end = value.length;
    for (let i = earlier; i < value.length; i++) {
      if (value[i] === '(') depth++;
      if (value[i] === ')' && --depth === 0) {
        end = i + 1;
        break;
      }
    }
    value = value.slice(0, earlier) + value.slice(end);
  }

  value = value.replace(/[^\p{L}\p{N}]+/gu, ' ');
  for (const form of LEGAL_FORMS) value = value.split(form).join(' ');
  value = value.replace(/\s+/g, ' ').trim().replace(LEGAL_ABBREVIATIONS, ' ').replace(LEGAL_ABBREVIATIONS, ' ');
  // «ПАО Сбербанк» и «ПАО «Сбербанк России»» (постановления ФССП, 28.10.2026) — один кредитор
  value = value.trim().replace(/(\S)\s+россии$/, '$1');
  return value.replace(/\s+/g, '');
}

export interface CreditorCandidate {
  /** Запись справочника, из которой строка была выбрана, если была */
  dictionaryId: string | null;
  name: string;
  /** Только корректный ИНН организации; неверный в справочник не несётся */
  inn: string;
  address: string;
}

/**
 * Строки дела, которые стоит внести в справочник.
 *
 * Нужны наименование и адрес: без адреса запись бесполезна — ради
 * подстановки адреса справочник и ведётся. Одинаковые кредиторы внутри
 * дела (два кредита в одном банке) схлопываются в одну запись.
 */
export function creditorsForDictionary(creditors: Creditor[]): CreditorCandidate[] {
  const result = new Map<string, CreditorCandidate>();

  for (const row of creditors) {
    const name = (row.name ?? '').trim().replace(/\s+/g, ' ');
    const address = (row.address ?? '').trim();
    const inn = (row.inn ?? '').replace(/\s/g, '');
    if (!name || !address) continue;

    if (inn.length === 12) continue; // гражданин или ИП
    if (inn.length !== 10 && looksLikePersonName(name)) continue;

    const key = creditorNameKey(name);
    if (result.has(key)) continue;
    result.set(key, {
      dictionaryId: row.dictionaryId ?? null,
      name,
      inn: isValidInn(inn, 'organization') ? inn : '',
      address,
    });
  }

  return [...result.values()];
}
